/**
 * MYAC-1176 — why a write to an invitation's project grants failed, in a form
 * the UI can translate.
 *
 * The store's own messages name the invitation id and its status enum, which
 * are exactly the server-sent identifiers the repo forbids in user-facing text
 * — and they are English whatever the reader's locale. So the failures a person
 * can actually act on carry a code instead, and the message stays a developer
 * detail for the log.
 */
export type InvitationWriteErrorCode = 'invitation_not_found' | 'invitation_not_editable'

interface CodedError extends Error {
    invitationWriteCode?: InvitationWriteErrorCode
}

export function invitationWriteError(code: InvitationWriteErrorCode, message: string): CodedError {
    const error: CodedError = new Error(message)
    error.invitationWriteCode = code
    return error
}

export function invitationWriteErrorCode(error: unknown): InvitationWriteErrorCode | null {
    const code = (error as CodedError | null)?.invitationWriteCode
    return code === 'invitation_not_found' || code === 'invitation_not_editable' ? code : null
}

/**
 * The i18n key explaining a failure, or `null` when there is nothing useful to
 * add beyond the headline. Never returns the raw message: a caller that renders
 * this is rendering translated text or nothing.
 */
export function invitationWriteErrorKey(error: unknown): string | null {
    const code = invitationWriteErrorCode(error)
    if (!code) return null
    return `settings.users.invitation_write_error.${code}`
}
